import { reduce } from '@fxts/core';
import { PG_COMMISSION_RATE } from '@modules/Creator/SettingAdjustments/S/constant';
import { MconAdjustmentForStudio } from '@services/MarppleShopStudio/features/core/adjustment/mcon-adjustment.ts';
import { CalcDecimalCore } from '@services/MarppleShopStudio/features/core/calc/calc-decimal.ts';

export class MconAdjustmentCore {
  static mcon_adjustment_picked_key = [
    'created_at',
    'profit',
    'quantity',
    'pg_fee',
    'service_fee',
    'products_mcon_themes_order_id',
  ] as const;

  /**
   * @desc
   * 테마 이미지 총 사용량 계산합니다.
   * 이미지 사용 개수 * 주문한 상품 개수으로 계산
   */
  static calcTotalQuantity(option: {
    /** 상품 이미지 사용량 */
    design_usage_quantity: number;
    /** 상품 주문 수량 */
    product_order_quantity: number;
  }): number {
    return option.design_usage_quantity * option.product_order_quantity;
  }

  /**
   * @desc
   * 정산 수익을 계산합니다.
   * 이미지당 가격 * 이미지 사용 개수 * 주문한 상품 개수으로 계산
   */
  static calcTotalProfit(option: {
    /** 이미지당 가격 */
    price: number;
    /** 상품 이미지 사용량 */
    design_usage_quantity: number;
    /** 상품 주문 수량 */
    product_order_quantity: number;
  }): number {
    return option.price * MconAdjustmentCore.calcTotalQuantity(option);
  }

  /**
   * @desc
   * pg 수수료를 계산합니다.
   * 크리에이터 디자인 주문 pg 수수료 * 이미지당 가격 * 이미지 사용 개수 * 주문한 상품 개수으로 계산
   */
  static calcTotalPgFee(option: {
    /** 이미지당 가격 */
    price: number;
    /** 상품 이미지 사용량 */
    design_usage_quantity: number;
    /** 상품 주문 수량 */
    product_order_quantity: number;
  }): number {
    return Math.round(CalcDecimalCore.mul(PG_COMMISSION_RATE, MconAdjustmentCore.calcTotalProfit(option)));
  }

  /**
   * @desc
   * 서비스 수수료를 계산합니다.
   * 크리에이터 디자인 주문 서비스 수수료 * 이미지당 가격 * 이미지 사용 개수 * 주문한 상품 개수으로 계산
   */
  static calcTotalServiceFee(option: {
    /** 서비스 수수료율 */
    service_fee_rate: string;
    /** 이미지당 가격 */
    price: number;
    /** 상품 이미지 사용량 */
    design_usage_quantity: number;
    /** 상품 주문 수량 */
    product_order_quantity: number;
  }): number {
    return Math.round(
      CalcDecimalCore.mul(option.service_fee_rate, MconAdjustmentCore.calcTotalProfit(option)),
    );
  }

  /**
   * @desc
   * 플러스와 크리에이터의 정산 수익을 계산합니다.
   * 크리에이터 수익 : 정산 수익 * 플러스와 크리에이터의 정산 비율
   * 플러스 수익 : 정산 수익 - 크리에이터 수익
   */
  static calcPlusCreatorProfit(option: {
    /** 정산될 전체 금액 */
    profit: number;
    /** 플러스와 크리에이터의 정산 비율 */
    seller_profit_share: number;
  }) {
    const creator_profit = Math.ceil(option.profit * option.seller_profit_share);

    return {
      creator_profit,
      plus_profit: option.profit - creator_profit,
    };
  }

  /**
   * @desc
   * 플러스와 크리에이터의 PG 수수료를 계산합니다.
   * 크리에이터 PG 수수료 : 정산 PG 수수료 * 플러스와 크리에이터의 정산 비율
   * 플러스 PG 수수료 : 정산 PG 수수료 - 크리에이터 PG 수수료
   */
  static calcPlusCreatorPgFee(option: {
    /** 정산될 전체 금액 */
    pg_fee: number;
    /** 플러스와 크리에이터의 정산 비율 */
    seller_profit_share: number;
  }) {
    const creator_pg_fee = Math.ceil(option.pg_fee * option.seller_profit_share);

    return {
      creator_pg_fee,
      plus_pg_fee: option.pg_fee - creator_pg_fee,
    };
  }

  /**
   * @desc
   * 플러스와 크리에이터의 서비스 수수료를 계산합니다.
   * 크리에이터 서비스 수수료 : 정산 서비스 수수료 * 플러스와 크리에이터의 정산 비율
   * 플러스 서비스 수수료 : 정산 서비스 수수료 - 크리에이터 서비스 수수료
   */
  static calcPlusCreatorServiceFee(option: {
    /** 정산될 전체 금액 */
    service_fee: number;
    /** 플러스와 크리에이터의 정산 비율 */
    seller_profit_share: number;
  }) {
    const creator_service_fee = Math.ceil(option.service_fee * option.seller_profit_share);

    return {
      creator_service_fee,
      plus_service_fee: option.service_fee - creator_service_fee,
    };
  }

  /**
   * @desc
   * 수익에서 수수료를 제한 금액을 계산합니다.
   */
  static calcWithoutCommissionAmount(adj: {
    /** 수익 금액 */
    profit: number;
    /** 서비스 수수료 */
    service_fee: number;
    /** pg 수수료 */
    pg_fee: number;
  }) {
    return adj.profit - adj.service_fee - adj.pg_fee;
  }

  /**
   * @desc
   * 수익에서 수수료를 제한 금액을 계산합니다.
   */
  static calcWithoutCommissionTotalAmount(
    adjustments: {
      /** 수익 금액 */
      profit: number;
      /** 서비스 수수료 */
      service_fee: number;
      /** pg 수수료 */
      pg_fee: number;
    }[],
  ) {
    return reduce(
      (acc, c) => CalcDecimalCore.plus(acc, MconAdjustmentCore.calcWithoutCommissionAmount(c)),
      0,
      adjustments,
    );
  }

  /**
   * @desc
   * 스튜디오 정산의 통합을 계산합니다.
   */
  static sumAdjustments(adjustments: MconAdjustmentForStudio[]): {
    amount: number;
    fee: number;
    pg_amount: number;
    without_commission_amount: number;
  } {
    return {
      amount: reduce((acc, c) => CalcDecimalCore.plus(acc, c.profit), 0, adjustments),
      fee: reduce((acc, c) => CalcDecimalCore.plus(acc, c.service_fee), 0, adjustments),
      pg_amount: reduce((acc, c) => CalcDecimalCore.plus(acc, c.pg_fee), 0, adjustments),
      without_commission_amount: MconAdjustmentCore.calcWithoutCommissionTotalAmount(adjustments),
    };
  }
}
